import { useState, useEffect } from 'react'
import { AdminClose } from '@/components/icons'
import { popModal, pushModal } from '@/components/modals'
import { Button } from '@/components/ui/button'
import {
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProjectStore } from '@/hooks/use-project-store'
import { removeProjectIdFromTableName } from '@/lib/supabase/utils'
import { fetcher } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs'
import { useTranslations } from 'next-intl'
import { ImportCsv } from './components/import-csv'
import { Loading } from './components/loading'
import { NormalDataTable } from './components/normal-data-table'
import { UserDataTable } from './components/user-data-table'
import { useDatabaseContext } from './context'
import { mockTables } from './mock'
import { usePreviewStore } from '@/hooks/use-preview-store'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useAICoding } from '@/hooks/use-ai-coding'
import type { ProjectMessage } from '@/lib/types'
import { nanoid } from 'nanoid'
import { useUserCoin } from '@/hooks/use-user-coin'
import { PaginationClass } from '@/shared'

export function DatabaseModal() {
  const t = useTranslations('database')
  const { project, aiStage, setPrompt, addMessage, api__appendMessage } =
    useProjectStore()
  const { data: user } = useCurrentUser()
  const { aiCoding } = useAICoding()
  const { checkOutOfCredit } = useUserCoin()

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isDataChange, setIsDataChange] = useState<boolean>(false)
  const { reloadPreviewPage } = usePreviewStore()

  // 添加页面浏览埋点
  useEffect(() => {
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.ADMIN_PAGE_VIEW,
      {
        page: 'admin_database',
        project_id: project?._id?.toString(),
      },
      user?._id?.toString(),
    )
  }, [user?._id, project?._id])

  const {
    tables,
    tableName,
    setTableName,
    usersData,
    isSupabaseEnabled,
    setNormalTablePagination,
    crud: { refetchList, selectedTableUrl, loading },
    loading: { users: isLoadingUsers, tables: isLoadingTables },
  } = useDatabaseContext()

  const triggerAiCoding = () => {
    setTimeout(() => {
      const textarea = document.getElementById('heyboss-ai-chat-textarea')
      if (textarea) {
        textarea.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            keyCode: 13,
            bubbles: true,
          }),
        )
      }
    }, 10)
  }

  const enableDatabase = () => {
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ADD_DATA_BUTTON_ADMIN,
      {
        action: 'enable_database',
        project_id: project?._id?.toString(),
      },
      user?._id?.toString(),
    )

    setPrompt(t('enable_prompt'))
    popModal()
    triggerAiCoding()
  }

  const handleCreateTableAndRecords = async (
    headers: string[],
    data: any[],
  ) => {
    const isOutOfCredit = await checkOutOfCredit()
    if (isOutOfCredit) {
      return
    }

    const id = nanoid()
    const addedUserMessage: ProjectMessage = {
      id,
      role: 'user',
      content: `create a table with columns ${headers.join(', ')}`,
      contentForAi: `create a new table with columns ${headers.join(', ')} and insert the following data: ${JSON.stringify(data)}`,
    }

    const newUserMessage = await api__appendMessage(addedUserMessage)
    if (!newUserMessage) {
      console.error('Failed to save create table message')
      return
    }
    addMessage(newUserMessage)
    aiCoding({ addedUserMessage: newUserMessage, codingType: 'database_alter' })
    popModal()
  }

  const classNames = {
    tabClassName:
      'opacity-40 flex items-center h-10 px-3 text-white text-xl font-medium mb-[24px] hover:text-[24px] hover:font-semibold data-[state=active]:text-[24px] data-[state=active]:font-semibold data-[state=active]:opacity-100',
  }

  return (
    <DialogContent
      className="sm:max-w-[100vw] h-[100vh] block overflow-hidden !rounded-none dark:border-transparent"
      noCloseButton
      overlayClassName="bg-black"
    >
      <DialogHeader className="h-12">
        <DialogTitle className="hidden">Admin</DialogTitle>
        <div className="flex gap-x-[20px]">
          <DialogClose>
            <AdminClose
              className="w-[28px] h-[28px] cursor-pointer"
              onClick={() => {
                isDataChange && reloadPreviewPage()
              }}
            />
          </DialogClose>
          <div className="text-white text-xl font-semibold">Admin</div>
        </div>
      </DialogHeader>

      <div className="h-px bg-gray-200 dark:bg-gray-800 mx-[-32px] w-[calc(100%+64px)]" />
      {isLoadingUsers || isLoadingTables ? (
        <Loading />
      ) : (
        tables &&
        tables.length === 0 &&
        usersData.length === 0 && (
          <div className="flex items-center justify-center w-full h-full">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('empty')}
              </p>
              <Button variant="active" onClick={enableDatabase}>
                {t('enable')}
              </Button>
            </div>
          </div>
        )
      )}

      {project && isSupabaseEnabled ? (
        ((tables && tables.length > 0) || usersData.length > 0) && (
          <div className="relative w-full h-full pb-7">
            <Tabs
              value={tableName}
              onValueChange={(value) => setTableName(value)}
              className="flex w-full h-full dark:border-[#232323] rounded-lg overflow-auto"
            >
              <TabsList className="flex flex-col justify-start w-60 h-full bg-transparent rounded-none border-r-[1px] border-r-[#232323] dark:border-l-[#232323] pt-4 relative">
                <div className="flex flex-col overflow-y-auto flex-1 pr-2 pb-16 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
                  {usersData.length > 0 && (
                    <TabsTrigger
                      key="users"
                      value="users"
                      data-table-name="users"
                      className={classNames.tabClassName}
                    >
                      users
                    </TabsTrigger>
                  )}
                  {tables &&
                    tables.length > 0 &&
                    tables.map((table) => (
                      <TabsTrigger
                        key={table.name}
                        value={table.name}
                        data-table-name={table.name}
                        className={classNames.tabClassName}
                      >
                        {removeProjectIdFromTableName(table.name, project)}
                      </TabsTrigger>
                    ))}
                </div>
                <div className="absolute bottom-[48px] w-full left-0">
                  <ImportCsv
                    onImportEnd={async (headers, data) => {
                      console.log(headers, data, 'import end')
                      handleCreateTableAndRecords(headers, data)
                    }}
                  />
                </div>
              </TabsList>
              <div className="flex flex-col flex-1 px-8 py-6 rounded-md">
                {usersData.length > 0 && (
                  <TabsContent
                    key="users"
                    value="users"
                    className="flex-1 relative"
                  >
                    {isLoadingUsers ? (
                      <Loading />
                    ) : (
                      <UserDataTable setIsDataChange={setIsDataChange} />
                    )}
                  </TabsContent>
                )}
                {tables &&
                  tables.length > 0 &&
                  tables.map((table) => (
                    <TabsContent
                      key={table.name}
                      value={table.name}
                      className="flex-1 relative"
                    >
                      {loading.LIST ? (
                        <Loading />
                      ) : (
                        <NormalDataTable
                          setIsDataChange={setIsDataChange}
                          onDataChange={async () => {
                            // 刷新当前列表数据
                            if (selectedTableUrl) {
                              await refetchList()
                              // 强制刷新当前页面数据
                              const newData = await fetcher(selectedTableUrl)
                              setNormalTablePagination((prev) => ({
                                ...prev,
                                [tableName]: new PaginationClass(
                                  newData.page,
                                  newData.perPage,
                                  newData.total,
                                ),
                              }))
                            }
                          }}
                        />
                      )}
                    </TabsContent>
                  ))}
              </div>
            </Tabs>
          </div>
        )
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="space-y-3 text-center">
            <Button
              size="lg"
              disabled={aiStage !== 'idle'}
              onClick={() => {
                sendToBothAnalytics(
                  MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ADD_DATA_BUTTON_ADMIN,
                  {
                    action: 'enable_database_secondary',
                    project_id: project?._id?.toString(),
                  },
                  user?._id?.toString(),
                )
                setPrompt(t('enable_prompt'))
                popModal('DatabaseModal')
                triggerAiCoding()
              }}
            >
              {t('enable')}
            </Button>
            <p className="max-w-lg text-sm text-gray-500 dark:text-white/60">
              {t('enable_description')}
            </p>
          </div>
        </div>
      )}
    </DialogContent>
  )
}
